// Contact Recipient Multi-Select - Gmail-like recipient picker for email composer
// Supports searching tenant contacts and entering raw email addresses

import {
  Avatar,
  AvatarFallback,
  Badge,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commander/ui';
import { cn } from '@commander/utils';
import { RiCloseLine, RiLoader4Line } from '@remixicon/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TenantContactSearchResult } from '../../api/tenantContactApi';
import { useSearchTenantContacts } from '../../hooks/queries/tenantContactQueries';
import type { StructuredRecipient } from '../../types';

interface ContactRecipientMultiSelectProps {
  value: StructuredRecipient[];
  onChange: (recipients: StructuredRecipient[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Optional location ID to scope contact search to a specific location */
  masterCompanyLocationId?: string;
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Debounce delay in milliseconds
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Get initials from a name string
 * Returns first letter of first and last name, or first two letters if single word
 */
function getInitials(name: string | undefined | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function ContactRecipientMultiSelect({
  value,
  onChange,
  placeholder = 'Search contacts or enter email...',
  disabled = false,
  masterCompanyLocationId,
}: ContactRecipientMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search term
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Search contacts using React Query (scoped by location when provided)
  const { data: searchResults, isLoading } = useSearchTenantContacts(
    debouncedSearchTerm,
    10,
    masterCompanyLocationId
  );

  // Filter out already-selected contacts from search results
  const filteredResults =
    searchResults?.filter(
      (contact) => !value.some((r) => r.address.toLowerCase() === contact.email.toLowerCase())
    ) ?? [];

  // Check if search term is a valid email that's not already added
  const isValidNewEmail =
    EMAIL_REGEX.test(searchTerm) &&
    !value.some((r) => r.address.toLowerCase() === searchTerm.toLowerCase());

  // Add a recipient (contact or raw email)
  const addRecipient = useCallback(
    (recipient: StructuredRecipient) => {
      // Prevent duplicates
      if (value.some((r) => r.address.toLowerCase() === recipient.address.toLowerCase())) {
        return;
      }
      onChange([...value, recipient]);
      setSearchTerm('');
      setDebouncedSearchTerm('');
    },
    [value, onChange]
  );

  // Remove a recipient
  const removeRecipient = useCallback(
    (address: string) => {
      onChange(value.filter((r) => r.address.toLowerCase() !== address.toLowerCase()));
    },
    [value, onChange]
  );

  // Handle selecting a contact from search results
  const handleSelectContact = useCallback(
    (contact: TenantContactSearchResult) => {
      addRecipient({
        address: contact.email,
        name: contact.displayName,
        tenantContactId: contact.id,
      });
      setOpen(false);
      inputRef.current?.focus();
    },
    [addRecipient]
  );

  // Handle adding raw email
  const handleAddRawEmail = useCallback(() => {
    if (isValidNewEmail) {
      addRecipient({
        address: searchTerm.trim(),
        name: undefined,
        tenantContactId: undefined,
      });
      setOpen(false);
      inputRef.current?.focus();
    }
  }, [searchTerm, isValidNewEmail, addRecipient]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      // Enter, Tab, or Comma to add recipient
      if ((e.key === 'Enter' || e.key === 'Tab' || e.key === ',') && searchTerm.trim()) {
        // If there's exactly one search result, select it (prioritize contact over raw email)
        if (filteredResults.length === 1) {
          e.preventDefault();
          handleSelectContact(filteredResults[0]);
          return;
        }

        // Otherwise try to add as raw email
        if (isValidNewEmail) {
          e.preventDefault();
          handleAddRawEmail();
          return;
        }

        // For Enter and Comma, always prevent default to avoid form submission/text entry
        // For Tab, allow normal focus navigation when there's nothing selectable
        if (e.key === 'Enter' || e.key === ',') {
          e.preventDefault();
        }
        return;
      }

      // Backspace to remove last recipient when input is empty
      if (e.key === 'Backspace' && !searchTerm && value.length > 0) {
        e.preventDefault();
        removeRecipient(value[value.length - 1].address);
      }

      // Escape to close dropdown
      if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [
      disabled,
      searchTerm,
      filteredResults,
      isValidNewEmail,
      value,
      handleSelectContact,
      handleAddRawEmail,
      removeRecipient,
    ]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      // Remove commas as they're used as separators
      setSearchTerm(newValue.replace(/,/g, ''));

      // Open dropdown when typing
      if (newValue.length >= 1 && !open) {
        setOpen(true);
      }
    },
    [open]
  );

  // Handle container click to focus input
  const handleContainerClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  return (
    <div
      data-recipient-container
      className={cn(
        'flex flex-wrap gap-1 items-center border rounded-md px-2 py-1.5 min-h-[38px] flex-1 cursor-text bg-background',
        disabled && 'opacity-50 cursor-not-allowed bg-muted',
        open && 'ring-2 ring-ring ring-offset-2'
      )}
      onClick={handleContainerClick}
    >
      {/* Selected recipient chips */}
      {value.map((recipient) => (
        <Badge key={recipient.address} variant="secondary" className="gap-1 pl-1 pr-1 py-0.5 h-6">
          {recipient.tenantContactId ? (
            <>
              {/* Contact chip with avatar */}
              <Avatar className="h-4 w-4">
                <AvatarFallback className="text-[8px]">
                  {getInitials(recipient.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs max-w-[150px] truncate">
                {recipient.name || recipient.address}
              </span>
            </>
          ) : (
            /* Raw email chip */
            <span className="text-xs max-w-[200px] truncate">{recipient.address}</span>
          )}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove ${recipient.name || recipient.address}`}
              className="ml-0.5 hover:bg-muted-foreground/20 rounded-full p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                removeRecipient(recipient.address);
              }}
            >
              <RiCloseLine className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}

      {/* Search input with popover */}
      {!disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <input
              ref={inputRef}
              type="text"
              aria-label="Search contacts or enter email addresses"
              className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => searchTerm.length >= 1 && setOpen(true)}
              placeholder={value.length === 0 ? placeholder : ''}
              disabled={disabled}
            />
          </PopoverTrigger>
          <PopoverContent
            className="w-[300px] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => {
              // Don't close if clicking within the input container
              const target = e.target as HTMLElement;
              if (target.closest('[data-recipient-container]')) {
                e.preventDefault();
              }
            }}
          >
            <Command shouldFilter={false}>
              <CommandList className="max-h-[400px]">
                {isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <RiLoader4Line className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">Searching...</span>
                  </div>
                ) : filteredResults.length > 0 ? (
                  <CommandGroup heading="Contacts">
                    {filteredResults.map((contact) => (
                      <CommandItem
                        key={contact.id}
                        value={contact.id}
                        onSelect={() => handleSelectContact(contact)}
                        className="cursor-pointer"
                      >
                        <Avatar className="h-8 w-8 mr-2">
                          <AvatarFallback>{getInitials(contact.displayName)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col overflow-hidden">
                          <span className="font-medium truncate">{contact.displayName}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {contact.email}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : debouncedSearchTerm.length >= 1 && !isLoading ? (
                  <CommandEmpty>
                    <div className="py-2 text-center">
                      {isValidNewEmail ? (
                        <button
                          type="button"
                          className="text-sm text-primary hover:underline"
                          onClick={handleAddRawEmail}
                        >
                          Add "{searchTerm}"
                        </button>
                      ) : (
                        <span className="text-sm text-muted-foreground">No contacts found</span>
                      )}
                    </div>
                  </CommandEmpty>
                ) : null}

                {/* Show "Add email" option when valid email is typed */}
                {isValidNewEmail && filteredResults.length > 0 && (
                  <CommandGroup heading="Add email">
                    <CommandItem
                      value={`add-${searchTerm}`}
                      onSelect={handleAddRawEmail}
                      className="cursor-pointer"
                    >
                      <span className="text-sm">Add "{searchTerm}"</span>
                    </CommandItem>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
